import React from "react";

import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  ListChecks,
  type LucideIcon,
  Users,
} from "lucide-react";

import {
  ACCENT,
  CollapsibleSection,
  MoveList,
  PointList,
  ScoreStrip,
} from "./Chrome";
import { ProfileCard } from "./ExecProfilesModal";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import type { XFactorExecProfile } from "../../../Types/XFactorViewsRendering";
import { SourceList } from "../../XFactorBriefings/components/Primitives";
import {
  buildExecProfiles,
  buildExecutiveBrief,
  matchExecProfile,
} from "../lib/derive";

/** Top-of-brief jump links — parent sections only (Full Briefing tab style). */
const ANCHORS: Array<{ id: string; label: string; icon: LucideIcon }> = [
  { id: "must-know", label: "Must know", icon: Lightbulb },
  { id: "watch-outs", label: "Watch-outs", icon: AlertTriangle },
  { id: "moves", label: "Recommended actions", icon: ListChecks },
  { id: "stakeholders", label: "Stakeholder map", icon: Users },
  { id: "questions", label: "Open questions", icon: HelpCircle },
];

const getScrollParent = (node: HTMLElement): HTMLElement | Window => {
  let parent = node.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return window;
};

/** Offset of a section from the top of the scroll container's content. */
const getSectionScrollTop = (root: HTMLElement, section: HTMLElement) => {
  const rootRect = root.getBoundingClientRect();
  const sectionRect = section.getBoundingClientRect();
  return sectionRect.top - rootRect.top + root.scrollTop;
};

/** Same rule as Guidance Library: last section whose heading has passed the read line. */
const resolveActiveFromScroll = (root: HTMLElement): string => {
  const readLine = root.scrollTop + 8;
  let current = ANCHORS[0].id;
  for (const a of ANCHORS) {
    const el = document.getElementById(`xf-brief-${a.id}`);
    if (!el) continue;
    if (getSectionScrollTop(root, el) <= readLine) current = a.id;
    else break;
  }
  return current;
};

/** Fixed modal subheader tabs — lives outside the scroll body. */
export const ExecutiveBriefSectionNav: React.FC = () => {
  const [activeAnchor, setActiveAnchor] = React.useState(ANCHORS[0].id);
  const activeAnchorRef = React.useRef(activeAnchor);
  activeAnchorRef.current = activeAnchor;
  const isClickScrolling = React.useRef(false);
  const scrollRafRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    let attachRaf = 0;
    let root: HTMLElement | Window | null = null;

    const applyFromScroll = () => {
      if (isClickScrolling.current || !(root instanceof HTMLElement)) return;
      const next = resolveActiveFromScroll(root);
      if (next !== activeAnchorRef.current) setActiveAnchor(next);
    };

    const onScroll = () => {
      if (isClickScrolling.current) return;
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        applyFromScroll();
      });
    };

    const attach = () => {
      if (cancelled) return;
      const first = document.getElementById(`xf-brief-${ANCHORS[0].id}`);
      if (!first) {
        attachRaf = window.requestAnimationFrame(attach);
        return;
      }
      root = getScrollParent(first);
      root.addEventListener("scroll", onScroll, { passive: true });
      applyFromScroll();
    };

    attach();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(attachRaf);
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      root?.removeEventListener("scroll", onScroll);
    };
  }, []);

  const jump = (id: string) => {
    const el = document.getElementById(`xf-brief-${id}`);
    if (!el) return;
    setActiveAnchor(id);
    isClickScrolling.current = true;
    const root = getScrollParent(el);
    if (root instanceof HTMLElement) {
      const sectionTop = getSectionScrollTop(root, el);
      root.scrollTo({
        top: Math.max(0, sectionTop - 8),
        behavior: "smooth",
      });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    window.setTimeout(() => {
      isClickScrolling.current = false;
    }, 800);
  };

  return (
    <nav
      className="flex w-fit max-w-full gap-x-2 overflow-x-auto bg-gray-100 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      aria-label="Executive Brief sections"
    >
      {ANCHORS.map(a => {
        const Icon = a.icon;
        const isActive = activeAnchor === a.id;
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => jump(a.id)}
            className={`inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap border-0 border-b-2 bg-transparent px-3 py-3 text-[13px] transition-colors ${
              isActive
                ? "border-[#8b5cf6] font-semibold text-[#8b5cf6]"
                : "border-transparent font-medium text-slate-500 hover:bg-white/40 hover:text-slate-600"
            }`}
          >
            <Icon
              size={14}
              strokeWidth={isActive ? 2.25 : 2}
              className="shrink-0"
              aria-hidden
            />
            <span>{a.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

/**
 * Step 2 of the ExecBridge flow — the long-form read.
 *
 * Every block is omitted rather than rendered empty when the payload has
 * nothing for it: a heading with no content reads as a bug, not as an absence.
 */
const ExecutiveBrief: React.FC<{
  accountName: string;
  data?: XFactorDigestData | null;
  /** When true, section tabs are rendered by the parent Modal subheader. */
  hideSectionNav?: boolean;
}> = ({ accountName, data, hideSectionNav = false }) => {
  const brief = React.useMemo(
    () => buildExecutiveBrief(accountName, data),
    [accountName, data]
  );
  const [sourcesOpen, setSourcesOpen] = React.useState(false);

  /**
   * The stakeholder map renders the same card as the leadership roster.
   *
   * A stakeholder the leadership section also profiles gets the full card —
   * tenure, bio, attribute chips and the DISC flip. One it does not gets a card
   * built from the brief's own fields, so the grid stays uniform either way.
   */
  const stakeholderProfiles: XFactorExecProfile[] = React.useMemo(() => {
    const execs = buildExecProfiles(data);
    return brief.stakeholders.map(s => {
      const matched = matchExecProfile(s.name, execs);
      if (matched) {
        return {
          ...matched,
          // The brief's summary is the account-facing read on this person and
          // is what the map is for; keep it ahead of the roster's biography.
          bio: s.summary || matched.bio,
        };
      }
      return {
        name: s.name,
        title: s.title,
        since: null,
        tenure: null,
        influence: null,
        bio: s.summary,
        previous: null,
        education: null,
        decisionMaking: null,
        attributes: [],
        refs: [],
        disc: null,
        decisionAttributes: [],
        influenceStyle: null,
      };
    });
  }, [brief.stakeholders, data]);

  if (!brief.present) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="font-medium">
          This briefing carries no Executive Brief.
        </span>{" "}
        xfactor&apos;s synthesis pass writes the brief into the payload; its
        absence means the pass did not complete for this digest, not that the
        account has nothing to say. The Situation Card and Account Synopsis may
        still have content.
      </div>
    );
  }

  return (
    <div>
      {hideSectionNav ? null : <ExecutiveBriefSectionNav />}

      <div id="xf-brief-glance">
        <CollapsibleSection title="Situation at a glance">
          <ScoreStrip tiles={brief.tiles} />
          {brief.glance ? (
            <p className="mt-3 rounded-lg bg-gray-50 px-4 py-3 text-sm leading-relaxed text-gray-800">
              {brief.glance}
            </p>
          ) : null}
          {brief.whyTheseScores ? (
            <p className="mt-2 text-sm leading-relaxed text-gray-600">
              {brief.whyTheseScores}
            </p>
          ) : null}
        </CollapsibleSection>
      </div>

      <div id="xf-brief-must-know">
        <CollapsibleSection title="Must know">
          {brief.customerWorld ? (
            <CollapsibleSection
              nested
              id="xf-brief-customer-world"
              title="Customer's world"
            >
              {brief.customerWorld.synopsis ? (
                <p className="mb-2 text-sm leading-relaxed text-gray-700">
                  {brief.customerWorld.synopsis}
                </p>
              ) : null}
              <PointList
                points={brief.customerWorld.items.map(i => ({
                  ...i,
                  implicationLabel: "Relevance to us",
                }))}
                citations={brief.citations}
                dense
                hideChip
                empty="No business lines in this payload."
              />
              {brief.customerWorld.whyTheyExist ? (
                <p className="mt-2 text-sm italic leading-snug text-gray-600">
                  {brief.customerWorld.whyTheyExist}
                </p>
              ) : null}
              {brief.customerWorld.strategicPosture ? (
                <p className="mt-2 text-sm leading-relaxed text-gray-700">
                  <span className="font-semibold">Strategic posture: </span>
                  {brief.customerWorld.strategicPosture}
                </p>
              ) : null}
            </CollapsibleSection>
          ) : null}

          {brief.businessHealth ? (
            <CollapsibleSection
              nested
              id="xf-brief-business-health"
              title="Business health"
            >
              {brief.businessHealth.trend ? (
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Trend: {brief.businessHealth.trend}
                </p>
              ) : null}
              {brief.businessHealth.synopsis ? (
                <p className="mb-2 text-sm leading-relaxed text-gray-700">
                  {brief.businessHealth.synopsis}
                </p>
              ) : null}
              <PointList
                points={brief.businessHealth.items}
                citations={brief.citations}
                dense
                hideChip
              />
              {brief.businessHealth.forward ? (
                <p className="mt-2 text-xs italic leading-snug text-gray-500">
                  Forward indicators: {brief.businessHealth.forward}
                </p>
              ) : null}
            </CollapsibleSection>
          ) : null}

          {!brief.customerWorld && !brief.businessHealth ? (
            <p className="text-xs italic text-gray-400">
              Neither the About nor the Financial section is present in this
              briefing.
            </p>
          ) : null}
        </CollapsibleSection>
      </div>

      <div id="xf-brief-watch-outs">
        <CollapsibleSection title="Watch-outs">
          <CollapsibleSection
            nested
            id="xf-brief-whats-changing"
            title="What's changing"
          >
            <PointList
              points={brief.whatsChanging}
              citations={brief.citations}
            />
          </CollapsibleSection>

          <CollapsibleSection nested id="xf-brief-landmines" title="Landmines">
            {brief.landmines.intro ? (
              <p className="mb-2 text-sm leading-relaxed text-gray-700">
                <span className="font-semibold">Posture: </span>
                {brief.landmines.intro}
              </p>
            ) : null}
            {brief.landmines.none ? (
              <p className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                The synthesis pass found no material landmines for this account.
              </p>
            ) : (
              <PointList
                points={brief.landmines.items}
                citations={brief.citations}
              />
            )}
          </CollapsibleSection>

          {brief.relationship ? (
            <CollapsibleSection
              nested
              id="xf-brief-relationships"
              title="Relationship implications"
            >
              <div className="mb-2 flex flex-wrap gap-2">
                {brief.relationship.motion ? (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                    Motion: {brief.relationship.motion}
                  </span>
                ) : null}
                {brief.relationship.urgency ? (
                  <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                    Urgency: {brief.relationship.urgency}
                  </span>
                ) : null}
              </div>
              {brief.relationship.summary ? (
                <p className="text-sm leading-relaxed text-gray-700">
                  {brief.relationship.summary}
                </p>
              ) : null}
              {brief.relationship.detail ? (
                <p className="mt-2 text-sm leading-relaxed text-gray-700">
                  {brief.relationship.detail}
                </p>
              ) : null}
              {brief.relationship.bullets.length ? (
                <ul className="mt-2 space-y-1">
                  {brief.relationship.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="text-xs leading-snug text-gray-600 before:mr-1.5 before:text-gray-300 before:content-['•']"
                    >
                      {b}
                    </li>
                  ))}
                </ul>
              ) : null}
              {brief.relationship.caution ? (
                <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-900">
                  Caution: {brief.relationship.caution}
                </p>
              ) : null}
            </CollapsibleSection>
          ) : null}
        </CollapsibleSection>
      </div>

      <div id="xf-brief-moves">
        {/* Same heading as the Situation Card's third column — the two views
            show the same plays, and calling them different things read as two
            different artifacts. The "n plays considered and dropped"
            disclosure is gone: it exposed the model's rejected options, which
            is workings rather than output. `droppedMoves` is still derived, so
            restoring it is a render change only. */}
        <CollapsibleSection title="Recommended actions and owners">
          <MoveList moves={brief.moves} />
        </CollapsibleSection>
      </div>

      <div id="xf-brief-stakeholders">
        <CollapsibleSection title="Stakeholder map">
          {brief.stakeholders.length ? (
            <div className="grid gap-3 md:grid-cols-2">
              {brief.stakeholders.map((s, i) => (
                <ProfileCard
                  key={i}
                  profile={stakeholderProfiles[i]}
                  footer={
                    s.angle ? (
                      <p className="text-xs leading-snug text-gray-500">
                        <span className="font-semibold">Angle: </span>
                        {s.angle}
                      </p>
                    ) : null
                  }
                />
              ))}
            </div>
          ) : (
            <p className="text-xs italic text-gray-400">
              No leadership profiles in this payload.
            </p>
          )}
        </CollapsibleSection>
      </div>

      <div id="xf-brief-questions">
        <CollapsibleSection title="Open questions">
          <PointList
            points={brief.openQuestions}
            citations={brief.citations}
            empty="No open questions in this payload."
          />
        </CollapsibleSection>
      </div>

      {brief.citations.length ? (
        <div className="mt-4 rounded-lg border border-gray-200">
          <button
            type="button"
            onClick={() => setSourcesOpen(o => !o)}
            aria-expanded={sourcesOpen}
            className="flex w-full items-center justify-between px-4 py-2.5"
          >
            <span
              className="text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: ACCENT }}
            >
              Sources ({brief.citations.length})
            </span>
            {sourcesOpen ? (
              <ChevronDown size={14} className="text-gray-400" aria-hidden />
            ) : (
              <ChevronRight size={14} className="text-gray-400" aria-hidden />
            )}
          </button>
          {sourcesOpen ? (
            <div className="border-t border-gray-100 px-4 py-3">
              <SourceList citations={brief.citations} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ExecutiveBrief;
